import React, { Fragment, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Message from 'supportAssistant/components/Chat/Message'
import MessagesLoadingSkeleton from './MessagesLoadingSkeleton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextLink from 'components/Luxkit/TextLink'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLoggedIn } from 'selectors/accountSelectors'
import moment from 'moment/moment'
import { ChatMessage, MessageSenders } from 'api/support'
import { SocketContext } from '../../contexts/SocketContext'
import Loading from 'components/Common/Loading/LoadingAnimation'

const StyledFooterBodyText = styled(BodyText)`
  word-break: break-word;
  white-space: pre-wrap;
  text-align: initial;
  margin-left: ${rem(8)};
`

const StyledDateBodyText = styled(BodyText)`
 margin-bottom: ${rem(24)};
 margin-top: ${rem(24)};
`

const StyledTimeBodyText = styled(BodyText)`
  margin-left: ${rem(34)};
  margin-bottom: ${rem(8)};
`

const MessagesContainerDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr;
`
const WAITING_MESSAGE_DELAY = 5000

const MessageContent = styled.div`
  padding: ${rem(8)};
  border-radius: ${rem(4)};
  background-color: ${({ theme }) => theme.palette.neutral.default.seven};
  color: ${({ theme }) => theme.palette.neutral.default.one};
  width: max-content;
`

interface Props {
  messages: ChatMessage[];
  welcomeMessagePlaceholder: ChatMessage | undefined;
  loadingMessages?: boolean;
  isTyping: boolean;
  chatCreatedAt: string | undefined;
  onMessageFeedbackSubmit: (feedback: { id: string, type: string }) => void;
}

function MessagesListContainer({
  messages,
  loadingMessages,
  welcomeMessagePlaceholder,
  isTyping,
  chatCreatedAt,
  onMessageFeedbackSubmit,
}: Props) {
  const userIsLoggedIn = useAppSelector(isLoggedIn)
  const [isTypingGtWait, setIsTypingGtWait] = useState(false)

  const { onLogin } = useContext(SocketContext)

  let previousDate = moment(chatCreatedAt).format('D MMMM YYYY')

  // we need to show welcome message placeholder until the actual message is created on chat creation event,
  // sending first message creates the welcome message and chat for non authed users and the first time authed users
  // this is done so that we don't create new chat each time someone opens support page,
  // instead if they send first message we create it
  const firstMessageLoading = isTyping && messages.length === 1

  // if isTyping has been true for longer than 5 seconds show text
  useEffect(() => {
    if (isTyping) {
      const timeout = setTimeout(() => {
        setIsTypingGtWait(true)
      }, WAITING_MESSAGE_DELAY)
      return () => clearTimeout(timeout)
    } else {
      setIsTypingGtWait(false)
    }
  }, [isTyping])

  return (
    <div>
      <MessagesContainerDiv>
        {loadingMessages && <MessagesLoadingSkeleton />}
        {!loadingMessages && <>
          <StyledDateBodyText variant="small" align="center">{moment(chatCreatedAt).format('D MMMM YYYY')}</StyledDateBodyText>
          <StyledTimeBodyText variant="small">{moment(chatCreatedAt).format('HH:MMa')}</StyledTimeBodyText>
          {(!chatCreatedAt || messages.length === 0 || firstMessageLoading) && <Message
              message={welcomeMessagePlaceholder}
              footer={
                !userIsLoggedIn && (
                  <StyledFooterBodyText variant="small">
                    To better assist you, please{' '}
                    <TextLink weight="regular" onClick={onLogin} size="small">
                      log in to Luxury Escapes
                    </TextLink>
                  </StyledFooterBodyText>
                )
              }
            />}
          {messages?.map((message, index) => {
            const messageDate = moment(message.createdAt).format('D MMMM YYYY')

            if (messageDate !== previousDate) {
              previousDate = messageDate

              return (
                <Fragment key={`${index}`}>
                  <StyledDateBodyText variant="small" align="center">
                    {previousDate}
                  </StyledDateBodyText>
                  <Message
                    footer={!userIsLoggedIn && index === 0 && <StyledFooterBodyText variant="small">To better assist you, please <TextLink weight="regular" onClick={onLogin} size="small">log in to Luxury Escapes</TextLink></StyledFooterBodyText>}
                    onMessageFeedbackSubmit={onMessageFeedbackSubmit}
                    message={message}
                    key={`${message.createdAt}-${index}`}
                  />
                </Fragment>
              )
            }

            return (
              <Message
                footer={!userIsLoggedIn && index === 0 && <StyledFooterBodyText variant="small">To better assist you, please <TextLink weight="regular" onClick={onLogin} size="small">log in to Luxury Escapes</TextLink></StyledFooterBodyText>}
                onMessageFeedbackSubmit={onMessageFeedbackSubmit}
                message={message}
                key={`${message.createdAt}-${index}`}
              />
            )
          })}
        </>}
        {isTypingGtWait && <Message
          message={{
            sender: MessageSenders.CHATBOT,
            text: 'We’re searching for a response to your question, thank you for your patience',
          }}
        />}
        {isTyping && <MessageContent><Loading size={6} /></MessageContent>}
      </MessagesContainerDiv>
    </div>
  )
}

export default MessagesListContainer
